import { OrderStatuses } from 'constants/OrdersTypes';
import { getQueryStringFromObject } from 'helpers/Query';
import { getPeriodsDates } from 'redux/utils/FiltersUtils';

export const ORDERS_COLUMN_LIMIT = 10;

export const ORDERS_BOARD_STATUSES = [
  OrderStatuses.WAITING_COMPANY,
  OrderStatuses.IN_PROCESS,
  OrderStatuses.READY,
  OrderStatuses.ON_WAY,
  OrderStatuses.ON_ADDRESS,
  OrderStatuses.DELIVERED,
  OrderStatuses.COMPLETED,
  OrderStatuses.REJECTED_COMPANY,
  OrderStatuses.REJECTED_CLIENT,
  OrderStatuses.NOT_DELIVERED,
];

export const getEmptyHomeColumn = () => ({
  results: [],
  count: 0,
  hasMore: false,
  isLoadingMore: false,
});

export const getEmptyHomeColumns = () => ORDERS_BOARD_STATUSES.reduce((acc, status) => {
  acc[status] = getEmptyHomeColumn();

  return acc;
}, {});

export const flattenHomeColumns = (columns = {}) => ORDERS_BOARD_STATUSES.reduce((acc, status) => {
  const column = columns[status];

  if (column?.results?.length) {
    return acc.concat(column.results);
  }

  return acc;
}, []);

export const parseStatusesFilter = (statuses) => {
  if (!statuses) {
    return [];
  }

  if (Array.isArray(statuses)) {
    return statuses.filter(Boolean);
  }

  return String(statuses)
    .split(',')
    .map((status) => status.trim())
    .filter(Boolean);
};

export const getStatusesToFetch = (statusesFilter) => {
  const selectedStatuses = parseStatusesFilter(statusesFilter);

  if (!selectedStatuses.length) {
    return ORDERS_BOARD_STATUSES;
  }

  return ORDERS_BOARD_STATUSES.filter((status) => selectedStatuses.includes(status));
};

export const buildOrdersColumnQuery = ({
  filters = {},
  status,
  offset = 0,
  limit = ORDERS_COLUMN_LIMIT,
}) => {
  const params = {
    limit,
    offset,
    statuses: status,
  };

  if (filters.search) {
    params.search = filters.search;
  }

  if (filters.location) {
    params.trade_point = filters.location;
  }

  if (filters.period) {
    const {
      start,
      end,
    } = getPeriodsDates(filters.period);

    if (start) {
      params.start_date = start;
    }

    if (end) {
      params.end_date = end;
    }
  }

  return `?${getQueryStringFromObject(params)}`;
};

export const isOrderMatchingLocationFilter = (order, locationFilter) => {
  if (!locationFilter) {
    return true;
  }

  if (!order || order.trade_point == null) {
    return false;
  }

  return String(order.trade_point) === String(locationFilter);
};

export const removeOrderFromColumns = (columns, orderId) => {
  let removedOrder = null;
  let removedFromStatus = null;
  const nextColumns = { ...columns };

  Object.keys(nextColumns)
    .forEach((status) => {
      const column = nextColumns[status] || getEmptyHomeColumn();
      const orderIndex = column.results.findIndex((order) => order.id === orderId);

      if (orderIndex >= 0) {
        removedOrder = column.results[orderIndex];
        removedFromStatus = status;
        nextColumns[status] = {
          ...column,
          results: column.results.filter((order) => order.id !== orderId),
          count: Math.max(0, (column.count || 0) - 1),
        };
      }
    });

  return {
    columns: nextColumns,
    removedOrder,
    removedFromStatus,
  };
};

export const upsertOrderInColumns = (columns, order) => {
  if (!order?.id) {
    return columns;
  }

  const {
    columns: nextColumns,
    removedOrder,
  } = removeOrderFromColumns(columns, order.id);
  const nextOrder = {
    ...removedOrder,
    ...order,
  };
  const targetStatus = nextOrder.status;

  if (!nextColumns[targetStatus]) {
    return nextColumns;
  }

  const targetColumn = nextColumns[targetStatus];

  nextColumns[targetStatus] = {
    ...targetColumn,
    results: [
      nextOrder,
      ...targetColumn.results,
    ],
    count: (targetColumn.count || 0) + 1,
  };

  return nextColumns;
};

export const updateOrderInColumns = (columns, order) => {
  if (!order?.id) {
    return columns;
  }

  const {
    columns: nextColumns,
    removedOrder,
    removedFromStatus,
  } = removeOrderFromColumns(columns, order.id);

  if (!removedOrder && !order.status) {
    return columns;
  }

  const nextOrder = {
    ...removedOrder,
    ...order,
  };
  const targetStatus = nextOrder.status || removedFromStatus;

  if (!nextColumns[targetStatus]) {
    return nextColumns;
  }

  const targetColumn = nextColumns[targetStatus];

  nextColumns[targetStatus] = {
    ...targetColumn,
    results: [
      nextOrder,
      ...targetColumn.results,
    ],
    count: (targetColumn.count || 0) + 1,
  };

  return nextColumns;
};
